import { ChangeEvent, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import type { TFunction } from 'i18next';

import {
  useGetExerciseProperties,
  useGetExerciseOptions,
} from '../../../../services/redux/api/exerciseConfigAPI';
import { useGetExerciseLoadFormulas } from '../../../../services/redux/api/appConfigApi';
import { ExercisePropertyType } from '../../../../shared/constants';
import type {
  ExerciseSyncItemDto,
  SyncExerciseRequestDto,
  SyncExerciseCreateDto,
} from '../../../../services/redux/api/syncApi';
import {
  useGetSyncRules,
  useGetMappingsQuery,
} from '../../../../services/redux/api/syncApi';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import { computeExerciseCreatePrefill } from '../../sync-rules/helpers/applySyncRule';
import { useGetPublicExercises } from '../../../../services/redux/api/exercisesAPI';
import type { SyncMappingDto } from '../../../../services/redux/api/syncApi';
import { INewFile } from '../../../../types/interfaces/new-file';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import yup from '../../../../helpers/yupInstance';

export type FormMode = 'link' | 'create';

export interface FormValues {
  localResourceId: string;
  createTitle: string;
  createDescription: string;
  createLanguageId: string;
  createCategoryId: string;
  createMainMuscleGroupId: string;
  createEquipmentId: string;
  createAdditionalMuscleIds: string[];
  createAdditionalEquipmentIds: string[];
  createDevelopedSkillIds: string[];
  createOptionIds: string[];
  createAvatarFileId: string | number | null;
  createSplitBySide: boolean;
  createExerciseLoadFormulaId: string;
}

function getSyncExerciseCreateValidation(t: TFunction) {
  return yup.object({
    createTitle: yup
      .string()
      .required(t('formikValidation.titleRequired'))
      .max(256, t('formikValidation.maxLength').replace('{0}', '256')),
    createLanguageId: yup
      .string()
      .required(t('formikValidation.languageRequired')),
    createCategoryId: yup
      .string()
      .required(t('formikValidation.categoryRequired')),
    createMainMuscleGroupId: yup
      .string()
      .required(t('formikValidation.mainMuscleGroupRequired')),
    createEquipmentId: yup
      .string()
      .required(t('formikValidation.equipmentRequired')),
    createOptionIds: yup
      .array()
      .max(4, t('formikValidation.exerciseOptionsMax').replace('{0}', '4')),
  });
}

export interface UseSyncExerciseFormParams {
  item: ExerciseSyncItemDto | null;
  provider: string;
  onSave: (
    body: SyncExerciseRequestDto,
    extra?: { filesToUpload: INewFile[]; avatarFileId: string | number | null },
  ) => void;
  onDirtyChange: (dirty: boolean) => void;
}

function sortPropertiesByPriorityOrName(
  list: IExerciseProperty[],
): IExerciseProperty[] {
  return [...list].sort((a, b) => {
    const aHasPriority = typeof a.priority === 'number';
    const bHasPriority = typeof b.priority === 'number';

    if (aHasPriority && bHasPriority) {
      return (a.priority ?? 0) - (b.priority ?? 0);
    }
    if (aHasPriority) return -1;
    if (bHasPriority) return 1;

    const aName = (a.name || a.nameKey || a.id || '').toLowerCase();
    const bName = (b.name || b.nameKey || b.id || '').toLowerCase();
    return aName.localeCompare(bName);
  });
}

function isGifFile(file: INewFile): boolean {
  return String(file.mimeType || '').toLowerCase() === 'image/gif';
}

const emptyCreate = {
  createTitle: '',
  createDescription: '',
  createLanguageId: '',
  createCategoryId: '',
  createMainMuscleGroupId: '',
  createEquipmentId: '',
  createAdditionalMuscleIds: [] as string[],
  createAdditionalEquipmentIds: [] as string[],
  createDevelopedSkillIds: [] as string[],
  createOptionIds: [] as string[],
  createAvatarFileId: null as string | number | null,
  createSplitBySide: false,
  createExerciseLoadFormulaId: '',
};

function getInitialValues(
  item: ExerciseSyncItemDto | null,
  prefill: {
    title: string;
    description: string;
    categoryId: string;
    mainMuscleGroupId: string;
    equipmentId: string;
    languageId: string;
    additionalMuscleIds: string[];
    additionalEquipmentIds: string[];
    developedSkillIds: string[];
  } | null,
  defaultLanguageId?: string,
): FormValues {
  if (!item) {
    return { localResourceId: '', ...emptyCreate };
  }

  const synced = Boolean(item.localResourceId);
  if (synced) {
    return {
      localResourceId: item.localResourceId ?? '',
      ...emptyCreate,
    };
  }

  return {
    localResourceId: '',
    createTitle: prefill?.title ?? '',
    createDescription: prefill?.description ?? '',
    createLanguageId: prefill?.languageId ?? defaultLanguageId ?? '',
    createCategoryId: prefill?.categoryId ?? '',
    createMainMuscleGroupId: prefill?.mainMuscleGroupId ?? '',
    createEquipmentId: prefill?.equipmentId ?? '',
    createAdditionalMuscleIds: prefill?.additionalMuscleIds ?? [],
    createAdditionalEquipmentIds: prefill?.additionalEquipmentIds ?? [],
    createDevelopedSkillIds: prefill?.developedSkillIds ?? [],
    createOptionIds: [],
    createAvatarFileId: null,
    createSplitBySide: false,
    createExerciseLoadFormulaId: '',
  };
}

export function useSyncExerciseForm({
  item,
  provider,
  onSave,
  onDirtyChange,
}: UseSyncExerciseFormParams) {
  const { t } = useTranslation();
  const { data: exerciseProperties = [] } = useGetExerciseProperties();
  const { data: exerciseOptions = [] } = useGetExerciseOptions();
  const { data: formulas = [] } = useGetExerciseLoadFormulas();
  const { data: exercises = [] } = useGetPublicExercises();
  const { data: languages = [] } = useGetLanguages();
  const { data: syncRules = [] } = useGetSyncRules();
  const { data: mappings = [] } = useGetMappingsQuery({ provider });

  const [mode, setMode] = useState<FormMode>('link');
  const [filesToUpload, setFilesToUpload] = useState<INewFile[]>([]);

  const categories = useMemo(
    () =>
      sortPropertiesByPriorityOrName(
        exerciseProperties.filter(
          (p) => p.type === ExercisePropertyType.category,
        ),
      ),
    [exerciseProperties],
  );
  const muscles = useMemo(
    () =>
      sortPropertiesByPriorityOrName(
        exerciseProperties.filter(
          (p) => p.type === ExercisePropertyType.muscle,
        ),
      ),
    [exerciseProperties],
  );
  const equipments = useMemo(
    () =>
      sortPropertiesByPriorityOrName(
        exerciseProperties.filter(
          (p) => p.type === ExercisePropertyType.equipment,
        ),
      ),
    [exerciseProperties],
  );
  const developedSkills = useMemo(
    () =>
      sortPropertiesByPriorityOrName(
        exerciseProperties.filter(
          (p) => p.type === ExercisePropertyType.developedSkill,
        ),
      ),
    [exerciseProperties],
  );

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        name: [c.nameKey, c.name].filter(Boolean).join(' — ') || c.id,
      })),
    [categories],
  );
  const muscleOptions = useMemo(
    () =>
      muscles.map((m) => ({
        id: m.id,
        name: [m.nameKey, m.name].filter(Boolean).join(' — ') || m.id,
      })),
    [muscles],
  );
  const equipmentOptions = useMemo(
    () =>
      equipments.map((e) => ({
        id: e.id,
        name: [e.nameKey, e.name].filter(Boolean).join(' — ') || e.id,
      })),
    [equipments],
  );

  const linkOptions = useMemo(
    () =>
      exercises.map((ex) => ({
        id: ex.id,
        label: ex.title || ex.id,
      })),
    [exercises],
  );

  const languageOptions = useMemo(
    () =>
      languages.map((l) => ({
        id: l.id,
        name: l.title || l.name || l.key || l.id,
      })),
    [languages],
  );

  const prefillFromMappings = useMemo(() => {
    if (!item || !provider || !syncRules.length) return null;
    return computeExerciseCreatePrefill({
      item,
      rules: syncRules,
      mappings: mappings as SyncMappingDto[],
      exerciseProperties,
      languages,
      provider,
      entity: 'exercise',
    });
  }, [
    item?.providerResourceId,
    provider,
    syncRules,
    mappings,
    exerciseProperties,
    languages,
  ]);

  const initialValues = useMemo(
    () => getInitialValues(item, prefillFromMappings),
    [item?.providerResourceId, item?.localResourceId, prefillFromMappings],
  );

  const validationSchema = useMemo(
    () => (mode === 'create' ? getSyncExerciseCreateValidation(t) : undefined),
    [mode, t],
  );

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (!item) return;
      if (mode === 'link' && values.localResourceId) {
        onSave({
          providerResourceId: item.providerResourceId,
          localResourceId: values.localResourceId,
        });
      }
      if (mode === 'create' && values.createTitle) {
        const hasFiles = filesToUpload.length > 0;
        const selectedAvatar = filesToUpload.find(
          (f) => String(f.id) === String(values.createAvatarFileId),
        );
        const isAvatarGif = selectedAvatar ? isGifFile(selectedAvatar) : false;
        const isAvatarValid =
          !hasFiles ||
          filesToUpload.some(
            (f) => String(f.id) === String(values.createAvatarFileId),
          );
        if (hasFiles && (!isAvatarValid || isAvatarGif)) {
          formik.setFieldError(
            'createAvatarFileId',
            t('formikValidation.exerciseAvatarRequired'),
          );
          return;
        }

        const create: SyncExerciseCreateDto = {
          title: values.createTitle.trim(),
          description: values.createDescription?.trim() || undefined,
          languageId: values.createLanguageId || undefined,
          categoryId: values.createCategoryId,
          mainMuscleGroupId: values.createMainMuscleGroupId,
          equipmentId: values.createEquipmentId,
        };
        if (values.createAdditionalMuscleIds.length > 0)
          create.additionalMuscleIds = values.createAdditionalMuscleIds;
        if (values.createAdditionalEquipmentIds.length > 0)
          create.additionalEquipmentIds = values.createAdditionalEquipmentIds;
        if (values.createDevelopedSkillIds.length > 0)
          create.developedSkillIds = values.createDevelopedSkillIds;
        if (values.createOptionIds.length > 0)
          create.optionIds = values.createOptionIds;
        if (values.createExerciseLoadFormulaId)
          create.exerciseLoadFormulaId = values.createExerciseLoadFormulaId;
        if (values.createAvatarFileId != null)
          create.avatarFileId = String(values.createAvatarFileId);
        create.splitBySide = values.createSplitBySide;

        onSave(
          {
            providerResourceId: item.providerResourceId,
            create,
          },
          {
            filesToUpload,
            avatarFileId: values.createAvatarFileId,
          },
        );
      }
    },
  });

  useEffect(() => {
    if (!item) return;
    setMode(Boolean(item.localResourceId) ? 'link' : 'create');
  }, [item?.providerResourceId, item?.localResourceId]);

  useEffect(() => {
    setFilesToUpload([]);
  }, [item?.providerResourceId]);

  const canSave = useMemo(() => {
    if (!item) return false;
    const { values } = formik;
    if (mode === 'link') {
      const value = values.localResourceId?.trim();
      if (!value) return false;
      return value !== (item.localResourceId ?? '');
    }
    if (mode === 'create') {
      if (!values.createTitle?.trim()) return false;
      if (!values.createLanguageId) return false;
      if (!values.createCategoryId) return false;
      if (!values.createMainMuscleGroupId) return false;
      if (!values.createEquipmentId) return false;
      return true;
    }
    return false;
  }, [item, mode, formik.values]);

  const hasUnsavedChanges = useMemo(() => {
    if (!item) return false;
    const { values } = formik;
    if (mode === 'link')
      return values.localResourceId !== (item.localResourceId ?? '');
    return formik.dirty || filesToUpload.length > 0;
  }, [
    item,
    mode,
    formik.dirty,
    formik.values.localResourceId,
    item?.localResourceId,
    filesToUpload.length,
  ]);

  useEffect(() => {
    onDirtyChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const selectedAdditionalMuscles = muscles.filter((m) =>
    formik.values.createAdditionalMuscleIds.includes(m.id),
  );
  const selectedAdditionalEquipments = equipments.filter((e) =>
    formik.values.createAdditionalEquipmentIds.includes(e.id),
  );
  const selectedDevelopedSkills = developedSkills.filter((s) =>
    formik.values.createDevelopedSkillIds.includes(s.id),
  );

  const availableFormulas = useMemo(() => {
    const selectedOptionIds = formik.values.createOptionIds;
    if (!formulas.length || !selectedOptionIds.length) return [];
    const selectedOptions = exerciseOptions.filter((opt) =>
      selectedOptionIds.includes(opt.id),
    );
    const selectedNameKeys = selectedOptions.map((opt) => opt.nameKey);
    return formulas.filter((formula) =>
      selectedNameKeys.some((key) => formula.value?.includes(key)),
    );
  }, [formulas, formik.values.createOptionIds, exerciseOptions]);

  function onChangeFormikOption(
    e: ChangeEvent<HTMLInputElement>,
    option: { id: string },
  ) {
    const prev = formik.values.createOptionIds;
    if (e.target.checked) {
      formik.setFieldValue('createOptionIds', [...prev, option.id]);
    } else {
      formik.setFieldValue(
        'createOptionIds',
        prev.filter((id) => id !== option.id),
      );
    }
    formik.setFieldValue('createExerciseLoadFormulaId', '');
  }

  function onFilesToUploadChanged(files: INewFile[]) {
    setFilesToUpload(files);
    formik.setFieldError('createAvatarFileId', undefined);
    if (files.length === 0) {
      formik.setFieldValue('createAvatarFileId', null);
    } else if (
      !files.some(
        (f) => String(f.id) === String(formik.values.createAvatarFileId),
      )
    ) {
      const firstNonGifFile = files.find((f) => !isGifFile(f));
      formik.setFieldValue('createAvatarFileId', firstNonGifFile?.id ?? null);
    }
  }

  function onMainFileChanged(id: string | number) {
    const selectedFile = filesToUpload.find(
      (file) => String(file.id) === String(id),
    );
    if (selectedFile && isGifFile(selectedFile)) {
      formik.setFieldError(
        'createAvatarFileId',
        t('formikValidation.exerciseAvatarRequired'),
      );
      return;
    }
    formik.setFieldValue('createAvatarFileId', id);
    formik.setFieldError('createAvatarFileId', undefined);
  }

  return {
    formik,
    mode,
    setMode,
    canSave,
    categories,
    muscles,
    equipments,
    developedSkills,
    categoryOptions,
    muscleOptions,
    equipmentOptions,
    linkOptions,
    languageOptions,
    selectedAdditionalMuscles,
    selectedAdditionalEquipments,
    selectedDevelopedSkills,
    exerciseOptions,
    filesToUpload,
    exerciseFiles: filesToUpload,
    onFilesToUploadChanged,
    onMainFileChanged,
    onChangeFormikOption,
    availableFormulas,
  };
}
